import { Component, Inject, Vue, Prop } from 'vue-property-decorator';
import { VERSION } from '@/constants';
import LoginService from '@/account/login.service';
import AccountService from '@/account/account.service';
import TranslationService from '@/locale/translation.service';
import NotificacionService from '@/entities/notificacion/notificacion.service';
import { ContextoNotificacion, Notificacion, TipoNotificacion } from '@/shared/formalizacion-client';
import SolicitudService from '@/entities/solicitud/solicitud.service';
import { PersonaFisica } from '@conacyt/cvu-client';
import { Authority } from '@/shared/security/authority';
import SolicitudImpl from '@/shared/model/solicitud.model';

@Component
export default class JhiNavbar extends Vue {
  @Inject('loginService')
  private loginService: () => LoginService;
  @Inject('translationService') private translationService: () => TranslationService;

  @Inject('accountService') private accountService: () => AccountService;
  @Inject('solicitudService') private solicitudService: () => SolicitudService;
  @Inject('notificacionService') private notificacionService: () => NotificacionService;
  public version = VERSION ? 'v' + VERSION : '';

  private currentLanguage = this.$store.getters.currentLanguage;
  public languages: any = this.$store.getters.languages;
  private hasAnyAuthorityValue = false;
  private pollingNotification: any;
  private oldNotificaciones: Array<Notificacion> = [];

  public notificaciones: Notificacion[] = [];
  public showNotificacion = false;
  public contadorNotificacion = 0;
  public personaFisica: PersonaFisica = null;
  public isMiicUser = false;
  public solicitante = false;
  public operador = false;
  public director = false;
  public coordinador = false;
  public secretarioTec = false;
  public secretarioAdm = false;
  public isFetching = false;
  public ableToSignNavber = false;

  created() {
    this.translationService().refreshTranslation(this.currentLanguage);
    this.hasAnyAuthorityInit();
  }

  beforeDestroy() {
    clearInterval(this.pollingNotification);
  }

  public subIsActive(input) {
    const paths = Array.isArray(input) ? input : [input];
    return paths.some(path => {
      return this.$route.path.indexOf(path) === 0; // current path starts with this path string
    });
  }

  public changeLanguage(newLanguage: string): void {
    this.translationService().refreshTranslation(newLanguage);
  }

  public isActiveLanguage(key: string): boolean {
    return key === this.$store.getters.currentLanguage;
  }

  public logout(): void {
    localStorage.removeItem('personaFisica');
    this.loginService()
      .logout()
      .then(response => {
        this.$store.commit('logout');
        clearInterval(this.pollingNotification);
        const data = response.data;
        let logoutUrl = data.logoutUrl;
        // if Keycloak, uri has protocol/openid-connect/token
        if (logoutUrl.indexOf('/protocol') > -1) {
          logoutUrl = logoutUrl + '?redirect_uri=' + window.location.origin;
        } else {
          // Okta
          logoutUrl = logoutUrl + '?id_token_hint=' + data.idToken + '&post_logout_redirect_uri=' + window.location.origin;
        }
        window.location.href = logoutUrl;
      })
      .catch(err => {
        window.location.href = '/';
      });
  }

  public openLogin(): void {
    this.loginService().login();
  }

  public get authenticated(): boolean {
    return this.$store.getters.authenticated;
  }

  public hasAnyAuthority(authorities: any): boolean {
    return this.accountService().hasAnyAuthority(authorities);
  }

  public get swaggerEnabled(): boolean {
    return this.$store.getters.activeProfiles.indexOf('swagger') > -1;
  }

  public get inProduction(): boolean {
    return this.$store.getters.activeProfiles.indexOf('prod') > -1;
  }

  public isTipoGeneral(notificacion: Notificacion): boolean {
    return notificacion.tipo === TipoNotificacion.GENERAL;
  }

  private getNuevasnotificaciones(notificaciones: Array<Notificacion>): Array<Notificacion> {
    return notificaciones.filter(notificacion => {
      return !this.oldNotificaciones.some(oldNotificacion => {
        return notificacion.id === oldNotificacion.id;
      });
    });
  }

  private getVariant(contexto: ContextoNotificacion): string {
    let variant: string;
    switch (contexto) {
      case ContextoNotificacion.PRIMARY:
        variant = 'primary';
        break;
      case ContextoNotificacion.SECONDARY:
        variant = 'secondary';
        break;
      case ContextoNotificacion.INFO:
        variant = 'info';
        break;
      case ContextoNotificacion.SUCCESS:
        variant = 'success';
        break;
      case ContextoNotificacion.WARNING:
        variant = 'warning';
        break;
      case ContextoNotificacion.DANGER:
        variant = 'danger';
        break;
      default:
        variant = 'default';
        break;
    }
    return variant;
  }

  private marcarNotificacionLeido(notificacion: Notificacion, index: number = null): void {
    this.notificacionService()
      .updateEstatusNotificacion(notificacion.id, notificacion)
      .then(res => {
        if (index) {
          this.notificaciones.splice(index, 1);
        }
      })
      .catch(err => {
        console.error('closeNotificacion');
        console.error(err);
      });
  }

  public closeNotificacion(index: number): void {
    const notificacion = this.notificaciones[index];
    if (notificacion) {
      notificacion.leido = true;
      this.marcarNotificacionLeido(notificacion, index);
    }
  }

  public mostrarNotificaciones(append = false): void {
    let noAutoHide = false;
    let index = 0;
    for (const notificacion of this.notificaciones) {
      const variant = this.getVariant(notificacion.contexto);
      if (
        notificacion.contexto === ContextoNotificacion.WARNING ||
        notificacion.contexto === ContextoNotificacion.DANGER ||
        notificacion.tipo === TipoNotificacion.ERROR_FIRMA
      ) {
        noAutoHide = true;
      }
      this.$bvToast.toast(notificacion.mensaje, {
        title: notificacion.titulo,
        autoHideDelay: 45000,
        noAutoHide,
        appendToast: append,
        variant,
      });
      this.marcarNotificacionLeido(notificacion, index);
      index++;
    }
    this.contadorNotificacion = 0;
  }

  private notificacionEscritorio(notificacion: Notificacion) {
    const defaultTitle = this.$t('home.logged.notificacion.defaultTitle').toString();
    if (!('Notification' in window)) {
      console.warn(this.$t('warning.notificaciones.browserSupport'));
    } else if (Notification.permission === 'granted') {
      const options = { body: notificacion.mensaje, icon: '/favicon.ico' };
      const titulo = notificacion.titulo ? notificacion.titulo : defaultTitle;
      const dNotification = new Notification(titulo, options);
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission(permission => {
        if (permission === 'granted') {
          const options = { body: notificacion.mensaje, icon: '/favicon.ico' };
          const titulo = notificacion.titulo ? notificacion.titulo : defaultTitle;
          const dNotification = new Notification(titulo, options);
        }
      });
    }
  }

  private getNotificaciones(): void {
    this.notificacionService()
      .getAllNotificaciones(null, false)
      .then(res => {
        const notificacionesNuevas = this.getNuevasnotificaciones(res.data);
        this.notificaciones = res.data;
        this.contadorNotificacion = res.data.length;
        for (const notificacion of notificacionesNuevas) {
          this.notificacionEscritorio(notificacion);
        }
        this.oldNotificaciones = this.oldNotificaciones.concat(notificacionesNuevas);
      })
      .catch(err => console.error(err));
  }

  private setNotificationInterval(): void {
    if (this.accountService().hasAnyAuthority([Authority.EXTERNAL_USER, Authority.OPERADOR, Authority.COORDINADOR, Authority.DIRECTOR])) {
      this.getNotificaciones();
      this.pollingNotification = setInterval(() => {
        this.getNotificaciones();
      }, 15000);
    }
  }

  public permisosBandeja(): void {
    this.ableToSignNavber = true;
    this.personaFisica = JSON.parse(localStorage.getItem('personaFisica'));

    if (this.operador) {
      this.ableToSignNavber = true;
    } else if (this.solicitante) {
      this.isFetching = true;
      this.solicitudService()
        .getParticipacionesSolicitudes()
        .then(res => {
          if (!SolicitudImpl.isExternoFondosBandeja(res.data)) {
            this.ableToSignNavber = false;
          }
          this.isFetching = false;
        })
        .catch(err => {
          console.error(this.$t('error.participacion.notFound'));
          this.ableToSignNavber = false;
          this.isFetching = false;
        });
    }
  }

  protected hasAnyAuthorityInit() {
    this.accountService()
      .hasAnyAuthorityAndCheckAuth(Authority.MIIC_USER)
      .then(val => {
        this.isMiicUser = val;

        if (this.isMiicUser) {
          if (!this.pollingNotification) {
            this.setNotificationInterval();
          }
          this.solicitante = this.accountService().userAuthorities.includes(Authority.EXTERNAL_USER);
          this.operador = this.accountService().userAuthorities.includes(Authority.OPERADOR);
          this.director = this.accountService().userAuthorities.includes(Authority.DIRECTOR);
          this.coordinador = this.accountService().userAuthorities.includes(Authority.COORDINADOR);
          this.secretarioTec = this.accountService().userAuthorities.includes(Authority.SECRETARIO_TEC);
          this.secretarioAdm = this.accountService().userAuthorities.includes(Authority.SECRETARIO_ADMON);

          this.permisosBandeja();
        } else {
          clearInterval(this.pollingNotification);
        }
      })
      .catch(err => {
        console.error(err);
      });
  }
}
